<template>
  <div class="patients">
    <Breadcrumbs :items="breadcrumbs" :adjustItemSize="true">
      <div class="items-container">
        <rp-dropdown
          v-model="roleComputed"
          :data-model="roleOption"
          objectLabel="name"
          placeholder="Role"
          class="items-dropdown"
          formStyle
        />

        <rp-dropdown
          v-if="isDistributor"
          v-model="countrySelected"
          :data-model="distributorCountries"
          objectLabel="name"
          placeholder="Countries"
          class="items-dropdown"
          formStyle
          scrollable
        />

        <rp-sprite v-if="isDownloadButtonVisible" @click.native="exportUsers" class="create-icon" name="get_app" />
      </div>
    </Breadcrumbs>

    <rp-table
      name="users"
      :results="getUsers"
      :filter="selectedRole + ' ' + countryComputed"
      :headings="headings"
      @rowClick="onRowClick">
    </rp-table>
  </div>
</template>

<script>

/* eslint-disable array-callback-return */


import Breadcrumbs from '@/components/Breadcrumbs'
import page from '@/mixins/page'

import Table from 'raypro_ui/src/components/ui/Table'

// API
import {getUsersByRoleAdmin, getUsersByRoleDistributor, getUsersByRoleHospitalAdmin} from 'raypro_ui/src/api/users'
import {getRoles, getRolesDistributorHospitalAdmin} from 'raypro_ui/src/api/roles'
import SelectField from 'raypro_ui/src/components/ui/inputs/StyledSelect'
import Dropdown from 'raypro_ui/src/components/ui/inputs/dropdown/Dropdown'
import Sprite from 'raypro_ui/src/components/ui/Sprite'

import {validationMixin} from 'vuelidate'
import {required} from 'vuelidate/lib/validators'
import {errorMessages} from 'raypro_ui/src/api/error_messages'
import {exportUsers, exportUsersDistributor, exportUsersHospitalAdmin} from 'raypro_ui/src/api/csvs'

import {mapGetters} from 'vuex'

export default {
  mixins: [validationMixin, page],
  components: {
    'rp-table': Table,
    'rp-select-field': SelectField,
    'rp-dropdown': Dropdown,
    'rp-sprite': Sprite,
    Breadcrumbs
  },

  data() {
    return {
      roleOption: [],
      role: '',
      roleLabel: '',
      countrySelected: '',
      selectedRole: 'surgeon',
      page: {
        totalPages: 1,
        totalEntries: 0,
        pageSize: 10,
        pageNumber: 1
      },
      headingsForAdmins: ['name', 'email', 'gender'],
      headingsForNurse: ['name', 'email', 'patients', 'gender'],
      headingsForSurgeons: ['name', 'email', 'patients', 'iols', 'gender'],
      headings: ['name', 'email', 'patients', 'iols', 'gender'],
      title: 'Users',
      breadcrumbs: [
        {label: 'Users'}
      ]
    }
  },

  computed: {
    ...errorMessages(['role']),

    ...mapGetters(['isDistributor', 'isHospitalAdmin', 'distributorCountries']),

    isDownloadButtonVisible() {
      return this.selectedRole === 'surgeon' || this.selectedRole === 'nurse'
    },

    roleComputed: {
      get() {
        return this.roleLabel + this.countrySelected.name
      },
      set(value) {
        if(value !== '') {
          this.role = value
          this.selectedRole = value.label
        }
        this.roleLabel = value
        if(this.role.name === 'Surgeon') {
          this.headings = this.headingsForSurgeons
        } else if(this.role.name === 'Nurse') {
          this.headings = this.headingsForNurse
        } else { this.headings = this.headingsForAdmins }
      }
    },

    countryComputed() {
      return this.countrySelected ? this.countrySelected.name : ''
    }
  },

  mounted() {
    if(this.isDistributor || this.isHospitalAdmin) {
      getRolesDistributorHospitalAdmin().then(({data}) => {
        data.map((role) => {
          if(role.name !== 'patient') {
            return this.roleOption.push({
              label: role.name,
              value: role.id,
              name: role.name.charAt(0).toUpperCase() + role.name.slice(1)
            })
          }
        })
        this.preselectRole()
      })
    } else {
      getRoles().then(({data}) => {
        data.map((role) => {
          if(role.name !== 'patient') {
            return this.roleOption.push({
              label: role.name,
              value: role.id,
              name: role.name.charAt(0).toUpperCase() + role.name.slice(1)
            })
          }
        })
        this.preselectRole()
      })
    }
  },

  validations() {
    const schema = {role: {required}}

    return schema
  },

  methods: {
    onRowClick(row) {
      this.$router.push(`/${row.role}s/${row.id}`)
    },

    preselectRole() {
      if(this.$route.params.role) {
        this.selectedRole = this.$route.params.role
        this.role = this.roleOption.find((el) => {
          return el.label === this.$route.params.role
        })
      }
    },

    getCountryPayload() {
      if(this.countrySelected) {
        const payload = `&countries=${this.countrySelected.id}`

        return payload
      }

      return ''
    },

    getUsers(page, userRole) {
      if(userRole) {
        let getUsersByRole
        let payload = this.selectedRole

        if(this.isDistributor) {
          payload += this.getCountryPayload()
          getUsersByRole = (page, payload) => { return getUsersByRoleDistributor(page, payload) }
        } else if(this.isHospitalAdmin) {
          getUsersByRole = (page, payload) => { return getUsersByRoleHospitalAdmin(page, payload) }
        } else {
          getUsersByRole = (page, payload) => { return getUsersByRoleAdmin(page, payload) }
        }

        return getUsersByRole(page, payload).then(({data, page}) => {
          const mapped = data.map((row) => {
            const data = {
              id: row.id,
              name: `${row.profile.first_name} ${row.profile.last_name}`,
              email: row.email,
              // TODO: map to age group
              dob: row.profile.dob,
              gender: row.profile.gender,
              role: row.roles[0].name,
              patients: row.patient_numbers,
            }

            if(this.selectedRole === 'surgeon') {
              data.iols = row.number_of_iols
            }

            return data
          })

          return {
            data: mapped,
            page
          }
        })
      }
    },

    exportUsers() {
      const roleToExport = this.selectedRole
      let exportUserPromise

      if(this.isDistributor) {
        exportUserPromise = (roleToExport) => { return exportUsersDistributor(roleToExport) }
      } else if(this.isHospitalAdmin) {
        exportUserPromise = (roleToExport) => { return exportUsersHospitalAdmin(roleToExport) }
      } else {
        exportUserPromise = (roleToExport) => { return exportUsers(roleToExport) }
      }

      exportUserPromise(roleToExport).then((res) => {
        const disposition = res.headers['content-disposition']
        const matches = /"([^"]*)"/.exec(disposition)
        const filename = matches !== null && matches[1] ? matches[1] : 'file.csv'
        const blob = new Blob([res.data], {type: 'text/csv'})
        const link = document.createElement('a')

        link.href = window.URL.createObjectURL(blob)
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
    },
  }
}
</script>

<style lang="sass" scoped>
  @import 'raypro_ui/src/styles/base/_variables.sass'

  .items-container
    display: flex;
    flex-direction: row;
    flex-grow: 1

    .items-dropdown
      width: 100%
      margin-right: 20px

    .styled-select
      width: auto
      flex-grow: 1

    .sprite
      margin: 35px 0 0 0
      min-width: 2.6rem

  /deep/ .input-wrapper
    input
      color: $color-text-invert
    .input-label
      color: $color-text-invert
      .has-focus
        color: $color-text-invert

</style>



// WEBPACK FOOTER //
// src/components/pages/Users/Users.vue